import { useEffect, useRef, useState } from 'react';
import { GraphContext } from './context';
import { createGraphStore, GraphStore } from '../../store';
import type { GraphId } from '../../types/graph';
import { useGraphLocation, useGraphResolver } from '../../hooks';
import { useStore } from '@/store';

interface GraphProviderProps extends React.PropsWithChildren {
  entryId: string;
  graphId?: GraphId;
}

export const GraphProvider = ({
  children,
  entryId,
  graphId,
}: GraphProviderProps) => {
  /** Global state */
  const globalStore = useStore();
  /** Navigation state */
  const { state } = useGraphLocation();
  /** Local state */
  const storeRef = useRef<GraphStore>();
  const [resolvedGraphId, setResolvedGraphId] = useState<GraphId | null>(null);
  /** Hooks */
  const { resolveGraph } = useGraphResolver(entryId, graphId);

  /**
   * We determine if a graph exists, if so fetch the graphId.
   * Otherwise create a new graph.
   */
  useEffect(() => {
    setResolvedGraphId(resolveGraph(state));
  }, [state, resolveGraph]);

  if (resolvedGraphId === null) {
    return null;
  }

  if (!storeRef.current) {
    storeRef.current = createGraphStore(globalStore, {
      id: resolvedGraphId,
    });
  } else {
    const currentGraphId = storeRef.current.getState().graph.getId();
    if (currentGraphId !== resolvedGraphId) {
      storeRef.current = createGraphStore(globalStore, {
        id: resolvedGraphId,
      });
    }
  }

  return (
    <GraphContext.Provider value={storeRef.current}>
      {children}
    </GraphContext.Provider>
  );
};
